import * as React from "react"
import { Check, Minus } from "lucide-react"

import { cn } from "@/lib/cn"

const Checkbox = React.forwardRef<
    HTMLInputElement,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'checked'> & {
        checked?: boolean | "indeterminate";
        onCheckedChange?: (checked: boolean | "indeterminate") => void;
    }
>(({ className, checked, onCheckedChange, disabled, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement>(null)

    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)

    React.useEffect(() => {
        const el = innerRef.current
        if (!el) return
        el.indeterminate = checked === "indeterminate"
    }, [checked])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onCheckedChange?.(event.target.checked)
    }

    const isOn = checked === true || checked === "indeterminate"

    return (
        <div className="relative flex h-4 w-4 shrink-0 items-center justify-center">
            <input
                type="checkbox"
                role="checkbox"
                aria-checked={checked === "indeterminate" ? "mixed" : checked === true}
                className={cn(
                    "peer h-4 w-4 shrink-0 rounded-sm border border-primary bg-white appearance-none ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    isOn && "border-blue-600 bg-blue-600",
                    className
                )}
                ref={innerRef}
                checked={checked === true}
                onChange={handleChange}
                disabled={disabled}
                {...props}
            />
            <Minus
                aria-hidden
                className={cn(
                    "pointer-events-none absolute inset-0 h-4 w-4 p-[5px] text-white",
                    checked === "indeterminate" ? "opacity-100" : "opacity-0"
                )}
                strokeWidth={3}
            />
            <Check
                aria-hidden
                className={cn(
                    "pointer-events-none absolute inset-0 h-4 w-4 p-0.5 text-white",
                    checked === true ? "opacity-100" : "opacity-0"
                )}
            />
        </div>
    )
})
Checkbox.displayName = "Checkbox"

export { Checkbox }
